import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { Button, FormLabel, Grid, MenuItem, Select, Typography } from '@mui/material';
import { Module } from '@thro-types/modules-selections';

import { Field, FieldArray, Formik } from 'formik';
import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ADD_STUDENT_SELECTION_MUTATION from 'gql/mutations/ModuleSelectionsService/addStudentSelection.gql';
import UPDATE_STUDENT_SELECTION_MUTATION from 'gql/mutations/ModuleSelectionsService/updateStudentSelection.gql';
import STUDENT_SELECTION_QUERY from 'gql/queries/ModuleSelectionsService/getStudentSelection.gql';
import USER_QUERY from 'gql/queries/currentUser.gql';

import GQLGuard from 'app/components/GQLGuard';
import SubmittingLoader from 'app/components/Util/SubmittingLoader';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';

import { SubjectType } from '../../../ModuleSelections/models';
import { StudentSelection } from '../../models';
import { studentModuleSelectionInitialValues, studentModuleSelectionSchema } from '../validation';
import { PickableModules } from './pickableModules';
import { SelectionInfoBox } from './selectionInfoBox';

export const StudentModuleSelection = ({
  maxSelectedModules,
  lengthOfTheWishlist,
  selectableModules,
  subjectType,
  moduleSelectionId
}: {
  maxSelectedModules: number;
  lengthOfTheWishlist: number;
  selectableModules: Module[];
  subjectType: SubjectType;
  moduleSelectionId: string;
}) => {
  const dispatch = useDispatch();
  const client = useApolloClient();
  const userData = client.readQuery({ query: USER_QUERY });
  const getStudentSelection = useQuery<
    { getStudentSelection: StudentSelection },
    { userId: number; moduleSelectionId: number }
  >(STUDENT_SELECTION_QUERY, {
    context: { clientName: 'federation' },
    variables: {
      moduleSelectionId: parseInt(moduleSelectionId, 10),
      userId: Number(userData.currentUser.id)
    }
  });

  const [createStudentSelection] = useMutation<{ addStudentSelection: StudentSelection }>(
    ADD_STUDENT_SELECTION_MUTATION,
    {
      context: { clientName: 'federation' }
    }
  );
  const [updateStudentSelection] = useMutation<StudentSelection>(
    UPDATE_STUDENT_SELECTION_MUTATION,
    { context: { clientName: 'federation' } }
  );

  //TODO Check if the student already selected his courses and use them as initial values
  const { t } = useTranslation();
  const pickableModulesLeft = Math.ceil(lengthOfTheWishlist / 2);
  const pickableModulesRight = lengthOfTheWishlist - pickableModulesLeft;
  const [submitting, setSubmitting] = useState(false);

  const handleSaveClick = async (values, setValues) => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (values.id) {
      await updateStudentSelection({
        variables: {
          data: {
            id: parseInt(values.id, 10),
            desiredNumberOfModules: values.desiredNumberOfModules,
            modulePreferences: values.modulePreferences
              .filter(modPref => modPref.moduleId !== null && modPref.moduleId !== undefined)
              .map((p, index) => ({
                moduleId: p.moduleId,
                rank: index + 1
              }))
          }
        },
        refetchQueries: [
          {
            query: STUDENT_SELECTION_QUERY,
            context: { clientName: 'federation' },
            variables: {
              moduleSelectionId: parseInt(moduleSelectionId, 10),
              userId: Number(userData.currentUser.id)
            }
          }
        ]
      })
        .then(() => {
          dispatch(
            AlertActions.success(t(translations.pages.studentView.alerts.wishlistSaveSuccess()))
          );
        })
        .catch(e => {
          dispatch(
            AlertActions.error(`${t(translations.pages.studentView.alerts.wishlistSaveFailed())}`)
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      try {
        const response = await createStudentSelection({
          variables: {
            data: {
              userId: parseInt(userData.currentUser.id, 10),
              desiredNumberOfModules: values.desiredNumberOfModules,
              modulePreferences: values.modulePreferences
                .filter(modPref => modPref.moduleId !== null && modPref.moduleId !== undefined)
                .map((p, index) => ({
                  moduleId: p.moduleId,
                  rank: index + 1
                })),
              moduleSelectionId: parseInt(moduleSelectionId, 10)
            }
          }
        });
        setValues(response.data?.addStudentSelection);
        dispatch(
          AlertActions.success(t(translations.pages.studentView.alerts.wishlistSaveSuccess()))
        );
      } catch (e) {
        dispatch(
          AlertActions.error(`${t(translations.pages.studentView.alerts.wishlistSaveFailed())}`)
        );
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <GQLGuard query={getStudentSelection} ignoreErrors={true}>
      {(data: { getStudentSelection: StudentSelection }) => {
        return (
          <Formik
            initialValues={studentModuleSelectionInitialValues(
              data?.getStudentSelection,
              maxSelectedModules
            )}
            validationSchema={yup.object().shape(studentModuleSelectionSchema(t))}
            onSubmit={() => {}}
            validateOnMount
          >
            {({ values, setValues, isValid }) => (
              <Grid item xs={12} paddingBottom={isMobile ? 4 : 0}>
                <Typography style={{ fontSize: '16px', paddingTop: '1em' }} component="h2">
                  {t(translations.pages.studentView.mySelection())}
                </Typography>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <FormLabel>
                      {t(translations.pages.studentView.numberOfModulesToSelect())}
                    </FormLabel>
                  </Grid>
                  <Grid item xs={12} md>
                    <Field
                      style={{ width: '100%' }}
                      as={Select}
                      name="desiredNumberOfModules"
                      value={values.desiredNumberOfModules}
                    >
                      {Array.from(Array(maxSelectedModules)).map((_, number) => {
                        return (
                          <MenuItem key={number + 1} value={number + 1}>
                            {number + 1}
                          </MenuItem>
                        );
                      })}
                    </Field>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <SelectionInfoBox subjectType={subjectType} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography style={{ fontSize: '16px' }} component="h2">
                      {t(translations.pages.studentView.wishlist())}
                    </Typography>
                    <FormLabel>{t(translations.pages.studentView.modulePrio())}</FormLabel>
                  </Grid>
                  <Grid item xs={12}>
                    <FieldArray
                      name="modulePreferences"
                      render={({ move, swap, push, insert, unshift, pop, remove }) => (
                        <Grid container direction="row" item xs={12} spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={2} direction="column">
                              {Array.from(Array(pickableModulesLeft)).map((_, index) => {
                                return (
                                  <Grid item xs={12} key={`${index}-left`}>
                                    <PickableModules
                                      position={index + 1}
                                      selectableModules={selectableModules}
                                      index={index}
                                      key={index}
                                      arrayHelpers={{
                                        move,
                                        swap,
                                        push,
                                        insert,
                                        unshift,
                                        pop,
                                        remove
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Grid container spacing={2} direction="column">
                              {Array.from(Array(pickableModulesRight)).map((_, index) => {
                                return (
                                  <Grid item xs={12} key={`${index}-right`}>
                                    <PickableModules
                                      position={index + pickableModulesLeft + 1}
                                      selectableModules={selectableModules}
                                      index={index + pickableModulesLeft}
                                      key={index + pickableModulesLeft}
                                      arrayHelpers={{
                                        move,
                                        swap,
                                        push,
                                        insert,
                                        unshift,
                                        pop,
                                        remove
                                      }}
                                    />
                                  </Grid>
                                );
                              })}
                            </Grid>
                          </Grid>
                        </Grid>
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    container
                    justifyContent="flex-end"
                    style={{ paddingBottom: '2em', paddingTop: '2em' }}
                  >
                    <Grid item>
                      <Button
                        key="submit"
                        variant="contained"
                        color="primary"
                        style={{ color: 'white' }}
                        disabled={submitting || !isValid}
                        onClick={() => {
                          handleSaveClick(values, setValues);
                        }}
                      >
                        {t(translations.common.save())}
                      </Button>
                      {submitting && <SubmittingLoader />}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Formik>
        );
      }}
    </GQLGuard>
  );
};
